/**
 * `F5` · `T-53` — El armazón de la caja, y el bloqueo por inactividad.
 *
 * Tres pantallas y una sola decisión que las ordena: **qué contesta
 * `/mostrador/personal`**.
 *
 *   · `401` → esta tablet no es una caja todavía → alta con SMS.
 *   · `200` → es una caja, y hay que saber quién entra → PIN.
 *   · con turno abierto → el campo único.
 *
 * ── Por qué el turno vive en memoria y no se recupera al recargar ─────────
 * La cookie de turno dura cinco minutos y es del servidor: esta app no puede
 * leerla. Podría preguntar «¿sigue abierto?» con una ruta nueva, y se decidió
 * que no: recargar la página de una tablet semipública y **volver a pedir el
 * PIN** es el comportamiento seguro, cuesta cuatro dígitos, y evita una ruta que
 * existiría sólo para eso. Lo que no se pierde es la caja, que dura un año.
 *
 * ── El bloqueo de los cinco minutos ───────────────────────────────────────
 * `R-09`, precisión del 2026-08-18: «bloqueo automático por inactividad, y
 * cambio de empleada en dos toques». Se cuenta desde el último gesto, no desde
 * que se entró: son cinco minutos de **descuido**, no de turno. Y al vencer se
 * cierra el turno de verdad —`DELETE /mostrador/turno`— en vez de sólo cambiar
 * de pantalla: una pantalla no revoca nada.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { INACTIVIDAD_DEL_TURNO_SEGUNDOS, type QuienPuedeEntrar, type TurnoAbierto } from '@fialrest/shared'
import { ErrorDeApi, mensajeParaLaPantalla } from '@fialrest/cliente'
import { cerrarTurno, quienPuedeEntrar } from './api.js'
import { Alta } from './Alta.js'
import { Turno } from './Turno.js'
import { Buscar } from './Buscar.js'

type Estado =
  | { readonly fase: 'cargando' }
  | { readonly fase: 'alta' }
  | { readonly fase: 'turno', readonly personal: QuienPuedeEntrar['personal'] }
  | { readonly fase: 'trabajando', readonly turno: TurnoAbierto }
  //  `T-70` · Caja montada, persona sin PIN. Se sale por SMS y se vuelve.
  | { readonly fase: 'activarse' }
  | { readonly fase: 'rota', readonly motivo: string }

interface Props {
  readonly slug: string
}

export function Mostrador({ slug }: Props): ReactNode {
  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' })
  const ultimoGesto = useRef(Date.now())

  const mirarLaCaja = useCallback(async (): Promise<void> => {
    try {
      const { personal } = await quienPuedeEntrar()
      setEstado({ fase: 'turno', personal })
    } catch (falla) {
      //  Un 401 aquí quiere decir «esta tablet no es una caja» —o su cookie
      //  caducó, o el dueño la revocó—, y los tres llevan al mismo sitio: dar
      //  de alta el aparato otra vez. Es deliberado que no se distingan.
      if (falla instanceof ErrorDeApi && falla.esSinSesion) { setEstado({ fase: 'alta' }); return }
      setEstado({
        fase: 'rota',
        motivo: mensajeParaLaPantalla(falla, 'No se pudo hablar con el servidor.'),
      })
    }
  }, [])

  useEffect(() => { void mirarLaCaja() }, [mirarLaCaja])

  const cerrar = useCallback(async (): Promise<void> => {
    //  Se ignora el fallo a propósito: cerrar un turno que ya caducó tiene que
    //  ser inofensivo, o cambiar de empleada fallaría justo cuando más falta
    //  hace que funcione.
    await cerrarTurno().catch(() => undefined)
    await mirarLaCaja()
  }, [mirarLaCaja])

  //  El reloj del descuido. Sólo corre mientras hay alguien dentro.
  useEffect(() => {
    if (estado.fase !== 'trabajando') return undefined
    const reloj = setInterval(() => {
      const quieta = (Date.now() - ultimoGesto.current) / 1000
      if (quieta >= INACTIVIDAD_DEL_TURNO_SEGUNDOS) void cerrar()
    }, 1000)
    return () => { clearInterval(reloj) }
  }, [estado.fase, cerrar])

  const marcarGesto = useCallback(() => { ultimoGesto.current = Date.now() }, [])

  if (estado.fase === 'cargando') {
    return <p className="caja-cargando">Un momento…</p>
  }

  if (estado.fase === 'rota') {
    return (
      <section className="caja-rota">
        <p role="alert">{estado.motivo}</p>
        <button type="button" onClick={() => { void mirarLaCaja() }}>Reintentar</button>
      </section>
    )
  }

  if (estado.fase === 'alta') {
    return <Alta slug={slug} alMontar={() => { void mirarLaCaja() }} />
  }

  //  `T-70` · La caja está montada y quien está delante todavía no tiene PIN.
  //  Misma pantalla y misma prueba —un SMS a su teléfono— pero **no monta nada**:
  //  volver a montar habría creado una caja fantasma en la lista del dueño.
  if (estado.fase === 'activarse') {
    return (
      <Alta
        slug={slug}
        para="mi-pin"
        alMontar={() => { void mirarLaCaja() }}
      />
    )
  }

  if (estado.fase === 'turno') {
    return (
      <Turno
        personal={estado.personal}
        alEntrar={(turno) => { marcarGesto(); setEstado({ fase: 'trabajando', turno }) }}
        alActivarse={() => { setEstado({ fase: 'activarse' }) }}
      />
    )
  }

  return (
    <main className="caja">
      <header className="caja__barra">
        <span className="caja__quien">{estado.turno.nombre}</span>
        <span className="caja__sucursal">{estado.turno.sucursal}</span>
        {/*  «Cambio de empleada en dos toques»: éste y el nombre de la siguiente. */}
        <button className="caja__salir" type="button" onClick={() => { void cerrar() }}>
          Cambiar de empleada
        </button>
      </header>
      <Buscar alTrabajar={marcarGesto} alPerderElTurno={() => { void cerrar() }} />
    </main>
  )
}
